import { Period } from "@/shared/domain/metrics";

import styles from "./DashboardHeader.module.css";
import { DashboardMenu, DashboardMenuProps } from "./DashboardMenu";
import { DashboardParams, TimeRange } from "./DashboardParams";

export interface DashboardHeaderProps {
  range: TimeRange;
  period: Period;
  onRangeChange: (range: TimeRange) => void;
  onPeriodChange: (period: Period) => void;
  /** Props forwarded verbatim to the saved-dashboard menu half of the
   *  header. Grouped rather than flattened so the menu's `onSelect` /
   *  `onSaved` names stay unambiguous next to the header's own params
   *  handlers, and so this interface tracks `DashboardMenuProps` without
   *  restating it. */
  menu: DashboardMenuProps;
}

/**
 * The dashboard's header row: the (range, period) selection controls plus
 * the saved-dashboard menu. Owns ALL positioning/sizing across screen sizes
 * (see DashboardHeader.module.css for the width-tier ladder and its
 * calibration); the children only render their controls.
 *
 * Composition is props-pass-through rather than JSX slots: the header must
 * size its children's boxes (slot classes on DashboardParams' two groups, a
 * sized wrapper div around DashboardMenu), so it renders them itself and
 * `MetricsDashboard` passes plain data.
 */
export function DashboardHeader({
  range,
  period,
  onRangeChange,
  onPeriodChange,
  menu,
}: DashboardHeaderProps) {
  return (
    /* The outer div is the `dashboard-header` size container; the inner div
     * is the flex row. They must be separate elements: container queries
     * only match descendants of the container, so the row could not restyle
     * itself (e.g. its gaps per width tier) if it carried the container. */
    <div className={styles.header}>
      <div className={styles.controls}>
        {/* Renders the range field + period group as two direct flex items
         * of this row (a fragment, not a wrapper box), sized via the slot
         * classes. */}
        <DashboardParams
          range={range}
          period={period}
          onRangeChange={onRangeChange}
          onPeriodChange={onPeriodChange}
          rangeClassName={styles.rangeSlot}
          periodClassName={styles.periodSlot}
        />
        <div className={styles.menuSlot}>
          <DashboardMenu {...menu} />
        </div>
      </div>
    </div>
  );
}
